import type {
  PurchaseOrder,
  PurchaseOrderBaseLine,
  PurchaseOrderLevel,
  PurchaseOrderStatus,
  PurchaseOrderUpdateBody,
} from '@/api/endpoints/purchase-orders'

/**
 * The draft's lines as the form holds them (CHG-2026-10-edi-purchase-orders, design D2, D12).
 *
 * A line orders whole packages of one level; "= Stück" is the base quantity the announced
 * delivery will carry. Everything here is pure, so the form and the tests share it.
 */
export interface EditLine {
  /** Stable across edits, so React keys survive. */
  key: string
  sku: string
  name: string | null
  packageLevel: number
  /** Raw input in whole packages; validated on save. */
  quantity: string
  levels: Array<PurchaseOrderLevel>
  /** The snapshot of the saved line, used while its level is not in `levels`. */
  saved: {
    unitName: string | null
    gtin: string | null
    contentPerUnit: number
  }
}

export const PURCHASE_ORDER_STATUS_VARIANT: Record<
  PurchaseOrderStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  SENDING: 'secondary',
  SENT: 'default',
  FAILED: 'destructive',
  DELIVERED: 'secondary',
}

let lineKeySeq = 0

export function editLinesFromOrder(order: PurchaseOrder): Array<EditLine> {
  return order.lines.map((line) => ({
    key: `po-line-${lineKeySeq++}`,
    sku: line.sku,
    name: line.name,
    packageLevel: line.packageLevel,
    quantity: String(line.quantity),
    levels: line.levels,
    saved: {
      unitName: line.unitName,
      gtin: line.gtin,
      contentPerUnit: line.contentPerUnit,
    },
  }))
}

/** The chosen level's name, GTIN and content; the saved snapshot when the level is unknown. */
export function currentLevel(line: EditLine): {
  name: string | null
  gtin: string | null
  contentPerUnit: number
} {
  const level = line.levels.find((l) => l.level === line.packageLevel)
  if (level)
    return {
      name: level.name,
      gtin: level.gtin,
      contentPerUnit: level.contentPerUnit,
    }
  return {
    name: line.saved.unitName,
    gtin: line.saved.gtin,
    contentPerUnit: line.saved.contentPerUnit,
  }
}

/** Whole packages, at least 1; null for anything else. */
export function parsePackageQuantity(input: string): number | null {
  const trimmed = input.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const value = Number(trimmed)
  return value >= 1 ? value : null
}

/** Exact to the thousandth, like every stock quantity (contents may be decimal, e.g. 2,5 kg). */
export function multiplyQuantity(packages: number, content: number): number {
  return Math.round(packages * Math.round(content * 1000)) / 1000
}

/** "= Stück" of a line: packages × content of the chosen level; null while the input is invalid. */
export function baseQuantityOf(line: EditLine): number | null {
  const packages = parsePackageQuantity(line.quantity)
  if (packages == null) return null
  return multiplyQuantity(packages, currentLevel(line).contentPerUnit)
}

/**
 * Another package level keeps roughly the same pieces: the base quantity is converted and rounded
 * up to whole packages (14 Tassen → Karton à 12 = 2 Kartons). An invalid quantity is left as typed.
 */
export function changeLevel(line: EditLine, packageLevel: number): EditLine {
  const target = line.levels.find((l) => l.level === packageLevel)
  if (!target || !target.orderable) return line
  const base = baseQuantityOf(line)
  const quantity =
    base == null || target.contentPerUnit <= 0
      ? line.quantity
      : String(
          Math.max(
            1,
            Math.ceil(
              Math.round(base * 1000) /
                Math.round(target.contentPerUnit * 1000),
            ),
          ),
        )
  return { ...line, packageLevel, quantity }
}

export interface DraftHeader {
  deliveryDate: string
  countsForPreorders: boolean
  note: string
}

export type UpdateBodyResult =
  | { ok: true; body: PurchaseOrderUpdateBody }
  | {
      ok: false
      error: 'deliveryDateRequired' | 'quantityInvalid'
      sku?: string
    }

/** The PUT body of a draft: replace-all lines, whole packages, the note trimmed to null. */
export function buildUpdateBody(
  header: DraftHeader,
  lines: Array<EditLine>,
  extra: Array<{ sku: string }> = [],
): UpdateBodyResult {
  if (header.deliveryDate.trim() === '')
    return { ok: false, error: 'deliveryDateRequired' }
  const out: PurchaseOrderUpdateBody['lines'] = []
  for (const line of lines) {
    const quantity = parsePackageQuantity(line.quantity)
    if (quantity == null)
      return { ok: false, error: 'quantityInvalid', sku: line.sku }
    out.push({ sku: line.sku, packageLevel: line.packageLevel, quantity })
  }
  // A newly added article: the server picks its smallest level with a GTIN.
  for (const added of extra) out.push({ sku: added.sku, quantity: 1 })
  const note = header.note.trim()
  return {
    ok: true,
    body: {
      deliveryDate: header.deliveryDate,
      countsForPreorders: header.countsForPreorders,
      note: note === '' ? null : note,
      lines: out,
    },
  }
}

/**
 * The Bestellvorschlag's Bestellung column as order lines (design D12): every row with a value
 * above 0, keyed `a:<sku>` (article) or `tg:<sku>` (planned Tagesgericht) — both name the sku that
 * is stocked and ordered. Values are base units as typed (de-AT comma allowed); the same sku twice
 * is added up.
 */
export function baseLinesFromOrders(
  orders: Record<string, string>,
): Array<PurchaseOrderBaseLine> {
  const bySku = new Map<string, number>()
  for (const [key, raw] of Object.entries(orders)) {
    const match = /^(?:a|tg):(.+)$/.exec(key)
    if (!match) continue
    const value = Number(raw.trim().replace(',', '.'))
    if (raw.trim() === '' || !Number.isFinite(value) || value <= 0) continue
    const sku = match[1]
    const millis =
      Math.round(value * 1000) + Math.round((bySku.get(sku) ?? 0) * 1000)
    bySku.set(sku, millis / 1000)
  }
  return [...bySku.entries()].map(([sku, baseQuantity]) => ({
    sku,
    baseQuantity,
  }))
}

/** The next weekday after `today` (yyyy-MM-dd), the default Liefertag of a new order. */
export function nextWeekdayIso(today: Date): string {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
